import { getProductImageURL } from '@noths/polaris-client-utils';

/** Roughly the first row of product cards, which is where the LCP element sits. */
export const PRELOADED_PRODUCT_IMAGE_COUNT = 3;

/**
 * Deliberately narrower than `Product`. The caller reads this off the serialised
 * hydration state, where every field is optional, so accept the loose shape and
 * skip anything without an image rather than force a cast at the call site.
 */
interface PreloadableProduct {
  images?: (string | undefined)[];
}

interface ProductPages {
  pages?: Record<number, (PreloadableProduct | undefined)[] | undefined>;
}

/**
 * Image URLs to preload for LCP: only the first few products of the first page.
 *
 * The count matters. A page holds 60 products, and emitting a
 * `<link rel="preload" as="image">` for every one of them makes the browser
 * fetch 60 full images at top priority, competing with the very LCP image the
 * preload exists to accelerate, and adding 60 URLs to every HTML response.
 *
 * Only each product's primary image is preloaded, never a secondary one. The card
 * loads every image after the first lazily whatever it is told, so preloading one
 * would fetch at top priority something the browser has not been asked to show,
 * which is the same self-defeating flood in a different shape.
 */
export const getPreloadProductImages = (products?: ProductPages): string[] => {
  const productPages = products?.pages ?? {};
  const [firstPageKey] = Object.keys(productPages);

  if (firstPageKey === undefined) {
    return [];
  }

  const firstProductPage = productPages[Number(firstPageKey)] ?? [];

  return (
    firstProductPage
      .slice(0, PRELOADED_PRODUCT_IMAGE_COUNT)
      .map((product) => product?.images?.[0])
      .filter((image): image is string => Boolean(image))
      /* Resolved with the same helper, and so at the same default size, as
         `adaptProductToProductCardProps` uses for the image the card renders. A
         hand-rolled `{size}` replacement here would skip the helper's double-encoding
         fix, and any URL it fixed would be preloaded under one name and rendered
         under another, costing a second fetch instead of saving the first. */
      .map((image) => getProductImageURL(image))
  );
};
